import ComparisonGrowthSelector from '@components/comparison-growth-selector/ComparisonGrowthSelector';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import {
  RelativePeriod,
  RelativePeriodLabel,
  RelativePeriodMapping,
} from '@components/comparison-period-selector/Types';
import WarningFeatureValueContainer from '@components/containers/warning-feature-value/WarningFeatureValueContainer';
import WarningProductGroupFilterContainer from '@components/containers/warning-pg-filter/WarningProductGroupFilterContainer';
import CountryAvailabilityBanner from '@components/country-availability-banner/CountryAvailabilityBanner';
import FilterChips from '@components/filter-chips/FilterChips';
import FilterSelectionNote from '@components/filter-selection-note/FilterSelectionNote';
import InfoModal from '@components/info-modal/InfoModal';
import LoadingOverlay from '@components/loading-overlay/LoadingOverlay';
import {
  constructCountryOption,
  getSelectedClientNameWithClientId,
  getSubscriptionForOtherProducts,
  mapDefaultSubscribedCountry,
  mapSubscribedCountries,
  onChangeHandlerForOtherProducts,
  OnRetailerChangeParam,
  saveErrorLogForOnChange,
} from '@components/multi-banner-retailer-selector/MultiBannerRetailerSelectorUtils';
import PageHeader from '@components/page-header/PageHeader';
import PgAvailabilityBanner from '@components/pg-availability-banner/PgAvailabilityBanner';
import { ExploreTabView, explorLinkConfig, initialNavigationLinkConfig } from '@components/tab-nav/TabConfig';
import TabNav, { NavigationLinkProps } from '@components/tab-nav/TabNav';
import TimeFramePickerWrapper from '@components/time-frame-picker/components/wrapper/TimeFramePickerWrapper';
import { applyTimeFramePicker } from '@components/time-frame-picker/utils/TimeFramePickerUtils';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { FilterToggleButton, useAnalytics } from '@ecosystem/newron-client-lib';
import { feedback, layouts } from '@ecosystem/newron-design-system';
import { SortingOptions } from '@components/design/migrated/Table/Sorting/types';
import { DefaultSortingOptionsForCategoryLeaderboard } from '@models/CategoryLeaderboard';
import { ClientSubscription } from '@models/Opa';
import { SaleType } from '@models/Products';
import { RetailerClient } from '@models/RetailerClient';
import { SalesPerformance } from '@models/SalesPerformance';
import { ExploreTabRestoreLocalSavedViewProps, ExploreViewState } from '@models/SavedViews';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { categoryLeaderboardConfiguration, performanceAnalysisConfiguration } from '@pages/Configuration';
import Segments from '@pages/explore/segments/Segments';
import Trends from '@pages/explore/trends/Trends';
import { ProductDataType, ProductType } from '@pages/product-analyzer/Product';
import { BaseTimePeriod, TimePeriod } from '@services/FiltersTypes';
import { getProductGroupsByCountry, getProductGroupsMasterByCountry } from '@services/ProductGroup.service';
import { updateInitialFiltersMarketPremium } from '@state/actions/initial-filters-market-premium/InitialFiltersMarketPremiumAction';
import { updateMarketPremiumRetailerClient } from '@state/actions/market-premium-retailer-client/MarketPremiumRetailerClientAction';
import {
  actionCreators,
  actionCreatorsHitlistsRetailerClient,
  actionCreatorsInitialFilters,
  actionCreatorsInitialFiltersProducts,
  actionCreatorsMarketPremium,
  actionCreatorsProducts,
  actionCreatorsRetailerClient,
  actionCreatorsRO,
  actionCreatorsSideFilters,
} from '@state/Index';
import { byCountry } from '@state/reducers/initial-filter/InitialFiltersPredicates';
import { MainFilterStateRO } from '@state/reducers/main-filter-retailer-overview/MainFiltersRoReducer';
import { MainFiltersState } from '@state/reducers/main-filter/MainFiltersReducer';
import { IRootState } from '@state/reducers/Reducers';
import { constructRetailerClientState } from '@state/reducers/retailer-client/RetailerClientUtils';
import { getAndStoreSavedFilters } from '@utils/AppSubscriptionUtils';
import {
  filterPGsByCountry,
  getCountriesByAccessibleProductGroup,
  getCountryFilterPairsByTimeFrame,
  updateCountryAndPGFilters,
} from '@utils/CountryUtils';
import { getExchangeRate } from '@utils/currencyUtils/CurrencyUtils';
import { DEFAULT_VOLUME, getVolumeBasedOnPeriodType } from '@utils/factVolumeUtils/FactVolumeUtils';
import { exploreResponseConfig } from '@utils/FilterSettings';
import {
  areOptionsTheSame,
  checkPGsByCountryPrecondition,
  constructCountryFilter,
  constructCountryOptionForSideMenu,
  findDefaultFilterBySelectedCountries,
  getFiltersForClient,
  getFiltersInitialState,
  getInitialAppliedCountries,
  getInitialSelectedAddFeat,
  getInitialSelectedAddFeatValues,
  getInitialSelectedSegFeat,
  getInitialSelectedSegFeatValues,
  getPeriodForFeatures,
  getSelectedAddFeatValues,
  getSelectedSegFeatValues,
  initialFeature,
  isRemovePricebandFilterForNonLocalCurrency,
  removeUnsavedFilters,
  UpdateAddFeatSegFeatValues,
  updateAppliedCurrencyFilterByCountry,
  updateFieldsForOverviewAndCategoryLeaderboardAndExplore,
  updatePgsByCountryOnCountryApplyWithProductType,
  updateSelectedProductGroup,
  validateStoreFilterSettings,
} from '@utils/FilterUtils';
import { useDispatchCountriesAvailability } from '@utils/hook/incrementalLoading/UseDispatchCountriesAvailability';
import { useDispatchPgsAvailability } from '@utils/hook/incrementalLoading/UseDispatchPgsAvailability';
import { useFetchSalesPerformance } from '@utils/hook/marketPerformance/UseFetchSalesPerformance';
import { useUserPersistedFilters } from '@utils/hook/saveFilter/UseUserPersistedFilters';
import { useRestoreExploreSavedView } from '@utils/hook/saveViews/UseRestoreExploreSavedView';
import { CATEGORY_FEATURE_OPTION } from '@utils/hook/selector/ShowPerformanceWithinSelector';
import { useSideMenuFilters } from '@utils/hook/sideFilters/SideFiltersHooks';
import {
  getDefaultLatestTimePeriodForMultiCountry,
  getLatestPeriodAmongAppliedCountries,
  getLatestPeriodByPeriodicityForSingleCountry,
  isSelectedPeriodExistInAllMultiCountries,
} from '@utils/multiCountryUtils/MultiCountryUtils';
import { TrackingGeneralProp } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  PAGE_VIEW_TRACKING_TITLE,
  pageViewTrackingFunction,
  PageViewTrackingFunctionParam,
} from '@utils/page-analytics/model/PageViewedTracking';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import {
  Country,
  CountryFilterPair,
  FeatureOption,
  FilterKeys,
  getPageTypeWithPageName,
  Option,
  PageName,
  PageType,
  PageTypeAndName,
  ShareType,
  SubSection,
} from '@utils/pageConfig/Types';
import { getFallbackPeriodCombo } from '@utils/period-set/PeriodSetFallback';
import { useInitializedPeriodSet } from '@utils/period-set/UseInitializedPeriodSet';
import { useUpdatePerfPeriodSet } from '@utils/period-set/UseUpdatePerfPeriodSet';
import {
  excludedPeriodicityForLineChart,
  getAndUpdatePeriod,
  getDefaultValueForDerivablePeriodicity,
  getPeriodicityForMATAndYTD,
  isBaseTimePeriodFormat,
} from '@utils/PeriodUtils';
import { isAuthorized } from '@utils/Permission';
import { NO_OPTIONS } from '@utils/StringConstants';
import { getGrandTotalValue } from '@utils/TotalUtils';
import { cloneDeep } from 'lodash';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux'; //NOSONAR typescript:S1874
import { useLocation } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import PageInfo from '../info/PageInfo';
import { Unauthorized } from '../unauthorized/Unauthorized';
import ExploreCopyViewButton from './copy-view-button/ExploreCopyViewButton';
import Leaderboard from './leaderboard/Leaderboard';
import { updateCountryPgSideFilter } from '@utils/UpdateCountryPgSideFilter';
import { KPIDropdownOption, kpiDropdownOptions } from '@models/PerformanceAnalysisboard';
import { mapStackedChartKPI } from '@data-visualization/stacked-chart/StackedChart';
import { initialiseBaseFilters } from '@utils/InitBaseFilters';
import { formatPriceBandAsPerExchangeRate } from '@utils/Formatters';
import { getCurrencyExchangeRate } from '@services/Currency.service';
import { useFactRevenueBySelectedCurrency } from '@hooks/UseFactRevenueBySelectedCurrency';

const supportedPeriodicities: Periodicity[] = categoryLeaderboardConfiguration.supportedPeriodicities;

interface Props {
  retailerClients: RetailerClient[];
}

export const TooltipsTextComponent: React.FC = () => {
  return (
    <section>
      <p>
        Get a comprehensive view of your market performance. Review all your categories in one place, including topline
        results, growth rates, and market shares. Easily switch between Chart and Table views, and customize reports
        with filters for countries and product groups. Click through for in-depth analysis on key factors in different
        segments, and track trends in brand performance and regional growth, all visualized on various charts for easy
        comprehension
      </p>
    </section>
  );
};

const Explore: React.FC<Props> = ({ retailerClients }) => {
  const CURRENT_TOTAL_NAME = getGrandTotalValue();
  const { pathname } = useLocation();
  const pagesConfig = usePageConfig();
  const { refetch } = useUserPersistedFilters();
  const { pgsByAppliedCountry, masterPgsByCountry } = pagesConfig;
  const [pgsByAppliedCountries, setPgsByAppliedCountries] = useState<CountryFilterPair[]>([]);
  const isAuthorizedRIUser = isAuthorized(pagesConfig.clientSubscription);
  const isAuthorizedRetailKadUser = isAuthorized(pagesConfig.retailKadClientSubscription);
  const isAuthorizedUser = isAuthorizedRIUser || isAuthorizedRetailKadUser;
  const { Page } = layouts;
  // Redux
  const dispatch = useDispatch();
  const { updateInitialFiltersProducts } = bindActionCreators(actionCreatorsInitialFiltersProducts, dispatch);
  const { updateInitialFilters } = bindActionCreators(actionCreatorsInitialFilters, dispatch);
  const { updateMainFiltersProducts } = bindActionCreators(actionCreatorsProducts, dispatch);
  const { updateMainFilters } = bindActionCreators(actionCreators, dispatch);
  const { updateMainFiltersRO } = bindActionCreators(actionCreatorsRO, dispatch);
  const { updateMainFiltersMarketPremium } = bindActionCreators(actionCreatorsMarketPremium, dispatch);
  const { updateHitlistsRetailerClient } = bindActionCreators(actionCreatorsHitlistsRetailerClient, dispatch);
  const { updateRetailerClient } = bindActionCreators(actionCreatorsRetailerClient, dispatch);
  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);
  const [isSubscribed, setIsSubscribed] = useState<boolean>(true);
  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );
  const effectiveProductType = getEffectiveProductType(subscribedProductTypes, PageName.EXPLORE);
  const retailerClientState = useSelector((state: IRootState) => state.retailerClient);
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const mainFilterStateProducts = useSelector((state: any) => state.mainFiltersProducts);
  const initialFiltersState = useSelector((state: IRootState) => state.initialFilters);
  const mainFiltersStateRO: MainFilterStateRO = useSelector((state: IRootState) => state.mainFiltersRO);
  const mainFiltersState: MainFiltersState = useSelector((state: IRootState) => state.mainFilters);

  const initialFiltersProductsState = useSelector((state: IRootState) => state.initialFiltersProducts);
  const hitlistsRetailerClientState = useSelector((state: IRootState) => state.hitlistsRetailerClient);

  const [groupingEffect, setGroupingEffect] = useState<boolean>(true);
  const {
    updatePeriodicity,
    updatePeriod,
    updateComparisonPeriod,
    updateCustomMonthsPeriodState,
    updateCustomWeeksPeriodState,
  } = useUpdatePerfPeriodSet();
  const getInitialLink = (): NavigationLinkProps => {
    const tabPath = pathname.split('/').filter(Boolean).pop() ?? '';
    return explorLinkConfig.find((tab) => tab.url === tabPath) ?? initialNavigationLinkConfig;
  };

  /*
   * Get initial state for side menu filters
   */
  const allFilters = [...pagesConfig.appliedFilters[PageType.EXPLORE].selectedFilters];
  const pgInitialState = getFiltersInitialState(allFilters, FilterKeys.PRODUCT_GROUP);
  const addFeatInitialState = getFiltersInitialState(allFilters, FilterKeys.ADDFEAT);
  const segFeatInitialState = getFiltersInitialState(allFilters, FilterKeys.SEGFEAT);

  const [selectedLink, setSelectedLink] = useState<NavigationLinkProps>(getInitialLink());

  const [selectedProductGroups, setSelectedProductGroups] = useState<Option[] | undefined>(pgInitialState?.options);

  const [appliedProductGroups, setAppliedProductGroups] = useState<Option[]>(
    pgInitialState !== undefined ? pgInitialState.options : [],
  );

  const { defaultCountry: initialCountry, defaultCurrency: initialCurrency } = initialiseBaseFilters(
    pagesConfig,
    effectiveProductType,
    updateSideFilters,
    PageType.EXPLORE,
  );

  const [appliedCountries, setAppliedCountries] = useState<Option[]>(initialCountry);

  const [selectedCountries, setSelectedCountries] = useState<Option[]>(initialCountry);

  const isMultiCountry = appliedCountries.length > 1;

  const filters = findDefaultFilterBySelectedCountries(
    pagesConfig.appliedFilters[PageType.EXPLORE].selectedFilters,
    initialFiltersState.initialFilters,
  );

  const { selectedGrowthType } = mainFiltersStateRO;
  const setGrowthOption = (growthType: GrowthTypes) => {
    const payload = {
      selectedGrowthType: growthType,
    };
    updateMainFiltersRO(payload);
  };

  const [selectedAddFeat, setSelectedAddFeat] = useState<string | undefined>(
    getInitialSelectedAddFeat(addFeatInitialState),
  );

  const [selectedAddFeatValues, setSelectedAddFeatValues] = useState<Option[] | undefined>(
    getInitialSelectedAddFeatValues(addFeatInitialState),
  );

  const [selectedSegFeat, setSelectedSegFeat] = useState<string | undefined>(
    getInitialSelectedSegFeat(segFeatInitialState),
  );

  const [selectedSegFeatValues, setSelectedSegFeatValues] = useState<Option[] | undefined>(
    getInitialSelectedSegFeatValues(segFeatInitialState),
  );

  const updatedInitialFiltersState = useMemo(
    () => ({
      ...initialFiltersState,
      initialFilters: initialFiltersState.initialFilters.map((filter) => ({
        ...filter,
        timePeriod: filter.timePeriod,
      })),
    }),
    [initialFiltersState],
  );

  //region variables and setters related to period set
  const periodDispatchActions = useUpdatePerfPeriodSet();

  const timePeriodOptions: TimePeriod = useMemo(() => {
    return isMultiCountry && appliedCountries.length > 1
      ? getLatestPeriodAmongAppliedCountries(appliedCountries, updatedInitialFiltersState)
      : updatedInitialFiltersState.initialFilters.filter(byCountry(appliedCountries[0].value))[0].timePeriod;
  }, [appliedCountries, initialFiltersState, isMultiCountry]);

  const { period, periodicity, comparisonPeriod, customMonthsPeriodState, customWeeksPeriodState } =
    useInitializedPeriodSet(timePeriodOptions);

  const [isTableView, setIsTableView] = useState<boolean>(true);
  const [isLineChartView, setIsLineChartView] = useState<boolean>(false);
  const [currentSortingOption, setCurrentSortingOption] = useState<SortingOptions>(
    DefaultSortingOptionsForCategoryLeaderboard,
  );

  const [feature, setFeature] = useState<FeatureOption>(mainFiltersState.feature);
  const [selectedShareType, setSelectedShareType] = useState<ShareType>(ShareType.MarketShare);

  const [selectedTrendsShareType, setSelectedTrendsShareType] = useState<ShareType>(ShareType.Absolute);

  const [selectedSaleType, setSelectedSaleType] = useState<SaleType>(SaleType.revenue);
  const [selectedAbsoluteType, setSelectedAbsoluteType] = useState<ShareType>(ShareType.Absolute);

  const [loadingOverlay, setLoadingOverlay] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [currentVolume, setCurrentVolume] = useState<string>(DEFAULT_VOLUME);

  const [revenuePerformance, setRevenuePerformance] = useState<SalesPerformance>(new SalesPerformance());
  const [volumePerformance, setVolumePerformance] = useState<SalesPerformance>(new SalesPerformance());
  const [isApiError, setIsApiError] = useState<boolean>(false);

  const [isFetchingCardApi, setIsFetchingCardApi] = useState<boolean>(false);
  const [loadingPosFacts, setLoadingPosFacts] = useState<boolean>(true);

  const [isRestoreFromSavedView, setIsRestoreFromSavedView] = useState<boolean>(false);
  const [segmentByFeature, setSegmentByFeature] = useState<FeatureOption>(initialFeature);
  const [kpi, setKpi] = useState<KPIDropdownOption>(() =>
    mapStackedChartKPI(
      pagesConfig.sortingOptionsPerformanceAnalysisStackedChart.headerKey,
      kpiDropdownOptions(currentVolume),
    ),
  );

  // Optional: Update KPI whenever volume or config changes
  useEffect(() => {
    const newKpi = mapStackedChartKPI(
      pagesConfig.sortingOptionsPerformanceAnalysisStackedChart.headerKey,
      kpiDropdownOptions(currentVolume),
    );
    setKpi(newKpi);
  }, [currentVolume, pagesConfig.sortingOptionsPerformanceAnalysisStackedChart.headerKey]);

  const { track } = useAnalytics();
  const [, setShowSelectedPeriodNotExistInCountriesWarningBox] = useState<boolean>( //NOSONAR typescript:S6754
    isMultiCountry
      ? !isSelectedPeriodExistInAllMultiCountries(initialFiltersState, period.label, periodicity, appliedCountries)
      : false,
  );

  useEffect(() => {
    if (isMultiCountry) {
      const isExist = isSelectedPeriodExistInAllMultiCountries(
        initialFiltersState,
        period.label,
        periodicity,
        appliedCountries,
      );

      setShowSelectedPeriodNotExistInCountriesWarningBox(!isExist);
    }
  }, [initialFiltersState, appliedCountries, periodicity, period]);

  useEffect(() => {
    (async () => {
      const res = await getCurrencyExchangeRate(period, pagesConfig, periodicity, initialCurrency[0]);
      pagesConfig.updateCurrencyExchangeRatePerCountry(res);
    })();
  }, [period]);

  useEffect(() => {
    const currencyExchangeRate = getExchangeRate(pagesConfig);
    const appliedFilters = cloneDeep(pagesConfig.appliedFilters);

    if (currencyExchangeRate > 0) {
      // Update PRICEBAND filter labels in applied filters
      appliedFilters[PageType.EXPLORE].selectedFilters.forEach((f) => {
        if (f.filterKey === FilterKeys.PRICEBAND) {
          f.options.forEach((k) => (k.label = formatPriceBandAsPerExchangeRate(k.value, currencyExchangeRate)));
        }
      });
      const selectedFilters = cloneDeep(pagesConfig.selectedFilters);
      // Replace only the PRICEBAND filter in selectedFilters
      const appliedPriceband = appliedFilters[PageType.EXPLORE].selectedFilters.find(
        (f) => f.filterKey === FilterKeys.PRICEBAND,
      );
      if (appliedPriceband) {
        selectedFilters[PageType.EXPLORE].selectedFilters = selectedFilters[PageType.EXPLORE].selectedFilters.map(
          (f) => (f.filterKey === FilterKeys.PRICEBAND ? appliedPriceband : f),
        );
        pagesConfig.setSelectedFilters(selectedFilters);
      }

      pagesConfig.setAppliedFilters(appliedFilters);
    }
  }, [pagesConfig.currencyExchangeRatePerCountry]);

  const isSelectMultiCountry = useMemo((): boolean => {
    return appliedCountries.length > 1;
  }, [pagesConfig.clientId, appliedCountries]);

  const trackPageView = () => {
    const trackingParam: PageViewTrackingFunctionParam = {
      eventTitle: PAGE_VIEW_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState,
      track,
      pageType: PageType.EXPLORE,
      pageName: PageName.EXPLORE,
      access: pagesConfig.access,
      subSection:
        selectedLink.value === ExploreTabView.Leaderboard
          ? SubSection.EXPLORE_LEADERBOARD
          : SubSection.EXPLORE_SEGMENTS,
      initialLocationId: getInitialAppliedCountries(pagesConfig, PageType.EXPLORE, effectiveProductType),
    };

    if (selectedLink.value !== ExploreTabView.Trends) {
      pageViewTrackingFunction(trackingParam);
    }
  };

  const toUpdateAddFeatSegFeatValues: UpdateAddFeatSegFeatValues = {
    setSelectedAddFeat: setSelectedAddFeat,
    setSelectedAddFeatValues: setSelectedAddFeatValues,
    setSelectedSegFeat: setSelectedSegFeat,
    setSelectedSegFeatValues: setSelectedSegFeatValues,
  };
  /*
   * update selectedProductGroup when selectedFilters change in context
   */
  useEffect(() => {
    updateSelectedProductGroup(
      pagesConfig,
      selectedCountries,
      setSelectedCountries,
      setSelectedProductGroups,
      toUpdateAddFeatSegFeatValues,
    );
  }, [pagesConfig.selectedFilters]);

  // Initial load
  useEffect(() => {
    const pageTypeAndName: PageTypeAndName = {
      pageType: PageType.EXPLORE,
      pageName: PageName.EXPLORE,
    };

    // Update page viewing
    updateRetailerClient({ ...retailerClientState, pageViewing: PageName.EXPLORE });

    // Since the currency filter is available on this page, we need to validate for any selected
    // priceband feature option.
    if (
      isRemovePricebandFilterForNonLocalCurrency(
        pagesConfig,
        sideFiltersState.appliedFilters[PageType.EXPLORE].selectedFilters,
        effectiveProductType,
        feature,
      ) ||
      feature.dataType === NO_OPTIONS
    ) {
      setFeature(CATEGORY_FEATURE_OPTION); // reset the feature
    }

    trackPageView();
    validateStoreFilterSettings(sideFiltersState, pagesConfig, pageTypeAndName);

    pagesConfig.setIsNavigateFromRS(true);
    pagesConfig.setIsHidePGInfoWarning(false);
    pagesConfig.setClearFilters(false);
    pagesConfig.setSelectedTimePeriod(period);

    updateFieldsForOverviewAndCategoryLeaderboardAndExplore(
      pagesConfig,
      appliedCountries,
      setSelectedCountries,
      setSelectedProductGroups,
      updateSideFilters,
    );
  }, []);

  useEffect(() => {
    const payload = {
      selectedGrowthType: selectedGrowthType,
      feature: feature,
    };

    const mainFilters = {
      timeFrame: periodicity,
      period,
      comparisonPeriod,
      selectedGrowthType,
      feature,
    };

    updateMainFiltersMarketPremium({
      ...mainFilters,
    });

    updateMainFilters(payload);
  }, [feature, selectedGrowthType]);

  //To check for feature flag on page load
  const getIsFeatureFlagEnabled = async () => {
    pagesConfig.setIsMultiCountryDropdown(true);
  };
  getIsFeatureFlagEnabled();

  /**
   * updates selectedProductGroup and appliedCountries when applied filters are changed
   *
   * REFACTOR: Perhaps we could remove this in the future. We are currently maintaining 2 (local and
   * context) states (appliedProductGroups & appliedCountries) that represent the same thing.
   */
  useEffect(() => {
    // Changed to retrieve the state from redux store instead of context
    const appliedFilters = sideFiltersState.appliedFilters[PageType.EXPLORE].selectedFilters;

    if (appliedFilters && appliedFilters.length !== 0) {
      const index = appliedFilters.findIndex((e: any) => e.filterKey === FilterKeys.PRODUCT_GROUP);
      index !== -1 ? setAppliedProductGroups(cloneDeep(appliedFilters[index].options)) : setAppliedProductGroups([]);
    } else {
      setAppliedProductGroups([]);
    }

    /**
     * Use this function to prevent setting the applied/selected countries state when the country
     * `Option` are the same with the currently selected ones.
     *
     * @param currOption - The current applied/selected countries.
     */
    const getEffectiveCountry = (currOption: Option[]) => {
      const initialAppliedCountries = getInitialAppliedCountries(pagesConfig, PageType.EXPLORE, effectiveProductType);
      return areOptionsTheSame(initialAppliedCountries, appliedCountries, 'value')
        ? currOption
        : initialAppliedCountries;
    };

    setAppliedCountries(getEffectiveCountry);
    setSelectedCountries(getEffectiveCountry);
  }, [pagesConfig.appliedFilters[PageType.EXPLORE].selectedFilters]);

  /**
   * Used to get main filters for the selected countries when country filter is changed
   *
   * REFACTOR: Can this be done on the callback when applying filters?
   */
  useEffect(() => {
    if (appliedCountries !== undefined && selectedCountries[0] !== undefined) {
      setGroupingEffect(true);
      const latestCountryTimePeriodOptions = getDefaultLatestTimePeriodForMultiCountry(
        periodicity,
        appliedProductGroups,
        appliedCountries,
        pgsByAppliedCountries,
        initialFiltersState,
      );
      //here is where it is updating the periodicity when applied countries changed
      const fallbackPeriod = getFallbackPeriodCombo(
        { period, periodicity: periodicity, comparisonPeriod: comparisonPeriod },
        supportedPeriodicities,
        latestCountryTimePeriodOptions,
        customMonthsPeriodState,
        customWeeksPeriodState,
      );

      if (fallbackPeriod !== null) {
        updatePeriod(fallbackPeriod.period);
        updatePeriodicity(fallbackPeriod.periodicity);
        updateComparisonPeriod(fallbackPeriod.comparisonPeriod);
      }

      getAndUpdatePeriod(fallbackPeriod ? fallbackPeriod.period : period, latestCountryTimePeriodOptions, updatePeriod);
    }
  }, [appliedCountries]);

  /**
   * Update side filter pg when selected countries is changed
   *
   */
  useEffect(() => {
    (async () => {
      const pgs = await getProductGroupsByCountry(
        retailerClientState.clientId,
        selectedCountries,
        effectiveProductType,
        undefined,
        false,
      );
      const pgsByCountry = filterPGsByCountry(pgs, selectedCountries);
      updateCountryAndPGFilters(
        pagesConfig,
        selectedCountries,
        pgsByCountry,
        exploreResponseConfig.response[0],
        effectiveProductType,
      );
    })();
  }, [selectedCountries]);

  /*
   * Updates selectedCountries when selected country is changed and differs from applied country
   * Updating the state will call another useEffect which helps to get the updated list of PGs
   *
   * This useEffect also (nastily) sets the initial default country on first load. Is there a way
   * to improve this?
   *
   * REFACTOR: Perhaps we could remove this in the future. We are currently maintaining 2 (local and
   * context) states (selectedProductGroups & selectedCountries) that represent the same thing.
   */
  useEffect(() => {
    updateFieldsForOverviewAndCategoryLeaderboardAndExplore(
      pagesConfig,
      selectedCountries,
      setSelectedCountries,
      setSelectedProductGroups,
      updateSideFilters,
    );
  }, [pagesConfig.selectedFilters[PageType.EXPLORE].selectedFilters]);

  useDispatchPgsAvailability({
    periodValue: period.value,
    periodicity,
    appliedCountries,
    pgsByAppliedCountry,
    countriesPgsAvailabilities: masterPgsByCountry,
    appliedProductGroups,
    isMultiCountry,
    feature,
  });

  useDispatchCountriesAvailability({
    periodValue: period.value,
    periodicity,
    appliedProductGroups,
    appliedCountries,
    pgsByAppliedCountry,
    masterPgsByCountry,
    isWeeklyMatYtdNonLocalCurrency: false,
  });

  /**
   * Refreshes Country and PG filter options in side menu filter
   */
  useEffect(() => {
    (async () => {
      await updateCountryPgSideFilter({
        retailerClientState,
        periodicity,
        appliedCountries,
        selectedCountries,
        effectiveProductType,
        pagesConfig,
        responseConfig: exploreResponseConfig,
      });
    })();
  }, [periodicity, appliedCountries, retailerClientState.clientId, effectiveProductType]);

  /**
   * will trigger this use effect to update the pgs by applied country when new countries and time frame is applied (*not selected)
   */
  useEffect(() => {
    (async () => {
      const { clientId } = retailerClientState;
      setPgsByAppliedCountries([]);
      if (
        !checkPGsByCountryPrecondition(
          pagesConfig,
          clientId,
          periodicity,
          sideFiltersState.appliedFilters,
          appliedCountries,
        )
      ) {
        return;
      }
      let availableMasterPgs = await getProductGroupsMasterByCountry(clientId, appliedCountries, effectiveProductType);
      pagesConfig.setMasterPgsByCountry(availableMasterPgs);
      updatePgsByCountryOnCountryApplyWithProductType(
        pagesConfig,
        clientId,
        selectedCountries,
        appliedCountries,
        effectiveProductType,
        undefined,
        setPgsByAppliedCountries,
      );
    })();
  }, [pagesConfig.pgsByCountry]);

  /**
   * Fetches add feature product features
   */
  useEffect(() => {
    const periodTimeFrame: Periodicity = getPeriodForFeatures(pagesConfig, periodicity, selectedCountries);
    getSelectedAddFeatValues({
      productType: effectiveProductType,
      selectedProductGroup: selectedProductGroups,
      selectedAddFeatValues: selectedAddFeatValues,
      selectedAddFeat: selectedAddFeat,
      initialFilters: initialFiltersState.initialFilters,
      selectedCountries,
      pagesConfig,
      selectedTimeFrame: periodTimeFrame,
    });
  }, [selectedAddFeatValues]);

  /**
   * Fetches seg feature product features
   */
  useEffect(() => {
    const periodTimeFrame: Periodicity = getPeriodForFeatures(pagesConfig, periodicity, selectedCountries);
    getSelectedSegFeatValues({
      productType: effectiveProductType,
      selectedProductGroup: selectedProductGroups,
      selectedSegFeatValues: selectedSegFeatValues,
      selectedSegFeat: selectedSegFeat,
      initialFilters: initialFiltersState.initialFilters,
      selectedCountries,
      pagesConfig,
      selectedTimeFrame: periodTimeFrame,
    });
  }, [selectedSegFeatValues]);

  const onFiltersTrayClickHandler = (isExpanded: boolean) => {
    pagesConfig.toggleFiltersTray(isExpanded);
    pagesConfig.setClearFilters(false);
    pagesConfig.setApplyFilters(false);
  };

  const countriesToQuery = useMemo(() => {
    const allowedCountries = isMultiCountry
      ? getCountriesByAccessibleProductGroup(appliedCountries, appliedProductGroups, pagesConfig, periodicity)
      : appliedCountries;

    return pagesConfig.clientCountries.filter((x) => allowedCountries.some((y) => x.id === y.value));
  }, [
    isMultiCountry,
    appliedCountries,
    pagesConfig.clientCountries,
    pagesConfig.pgsByCountry,
    appliedProductGroups,
    periodicity,
  ]);

  const [queryCountries, setQueryCountries] = useState<Country[]>(countriesToQuery);

  useEffect(() => {
    if (countriesToQuery.length === 0) {
      return;
    }

    setQueryCountries(countriesToQuery);
  }, [countriesToQuery]);

  /**
   * @remarks
   * useEffect to update current dynamic volume based on period type, default to UNITS
   *
   * **/
  useEffect(() => {
    setCurrentVolume(getVolumeBasedOnPeriodType(filters, periodicity));
  }, [filters, periodicity]);

  /**
   * To handle the change event when select other retailer in the retailer dropdown - reset the necessary states in order to refresh the reports
   * @param subscriptions client subscription from opa policy
   */
  const onChangeHandler = async (subscriptions: ClientSubscription[]) => {
    /**
     * @remark
     * Related to grouping to update state
     * groupingEffect - to prevent the useEffect for grouping leaderboard API from being triggered
     * when multibanner changed
     * **/
    setGroupingEffect(true);

    // Default clientId, but the clientId should be always the same no matter how many countries the client has
    const defaultClientId = subscriptions[0].retailer;

    const subscribedCountries = mapSubscribedCountries(subscriptions);

    // Get the current side menu filters selection from pagesConfig
    const pagesConfigAllFilters = [...pagesConfig.appliedFilters];

    // get hitlists subscription. current assumption is that it exist
    const hitlistsSubscriptions = getSubscriptionForOtherProducts(
      ProductType.ProductAnalyzer,
      pagesConfig,
      defaultClientId,
    );
    const hitlistsSubscribedCountries = mapSubscribedCountries(hitlistsSubscriptions);

    //Retrieve client name
    const selectedClientName = getSelectedClientNameWithClientId(retailerClients, defaultClientId);

    // Default country, retain the countries selection if exists in the subscription
    const defaultCountry = mapDefaultSubscribedCountry(subscriptions, appliedCountries);
    const defaultCountryCode = defaultCountry.countryCode;

    if (hitlistsSubscribedCountries.length !== 0) {
      const onChangeCLMethodParam: OnRetailerChangeParam = {
        appliedCountries,
        defaultClientId,
        pagesConfig,
        pagesConfigAllFilters,
        subscriptions: hitlistsSubscriptions,
        productType: ProductType.ProductAnalyzer,
        productDataType: ProductDataType.ProductAnalyzer,
        subscribedCountries: hitlistsSubscribedCountries,
        timeFrame: periodicity,
        pageName: PageName.EXPLORE,
        pageList: [PageType.EXPLORE],
        setLoadingOverlay,
      };

      await onChangeHandlerForOtherProducts(onChangeCLMethodParam).then((response) => {
        if (!response) {
          return;
        }
        // if update to react 18 and above, can remove call to batch function
        batch(() => {
          updateHitlistsRetailerClient(
            constructRetailerClientState(defaultClientId, hitlistsSubscribedCountries, selectedClientName),
          );
          updateInitialFiltersProducts({ initialFilters: response.filtersData });
          updateMainFiltersProducts({
            timeFrame: response.currTimeFrame,
            period: response.currPeriod,
            comparisonPeriod: RelativePeriod.PreviousYearCurrentPeriod,
            selectedGrowthType: mainFilterStateProducts.selectedGrowthType,
            country: response.defaultCountryCode,
          });
        });
      });
      constructCountryOptionForSideMenu(pagesConfigAllFilters, defaultCountry, [PageType.RI_PRODUCT]);
    }

    const filtersData = await getFiltersForClient(
      defaultClientId,
      subscribedCountries,
      setLoadingOverlay,
      effectiveProductType,
      ProductDataType.PerformanceAnalyzer,
      [],
    );

    const filtersDataBasedOnCountry = filtersData.find((f) => f.country === defaultCountryCode);

    // Display saved filter according to selected retailer for both product types
    await getAndStoreSavedFilters(effectiveProductType, defaultClientId, pagesConfig, pagesConfig.userId);

    if (!filtersDataBasedOnCountry) {
      saveErrorLogForOnChange(pagesConfig.clientCountries, pagesConfig.appliedFilters, PageName.EXPLORE);
      return;
    }

    constructCountryOptionForSideMenu(pagesConfigAllFilters, defaultCountry, [PageType.EXPLORE]);

    if (pagesConfig.isMultiCountryFeatureFlagPA) {
      // Construct new currency filter based on applied default country
      const currentlyAppliedFilters = pagesConfigAllFilters[PageType.EXPLORE].selectedFilters;
      updateAppliedCurrencyFilterByCountry(
        pagesConfig,
        constructCountryOption(defaultCountry)[0],
        currentlyAppliedFilters,
      );
    }

    // Construct state to update Redux Store
    const payload = {
      selectedGrowthType,
      defaultCountryCode,
    };
    const storeFilterSettings = {
      appliedFilters: pagesConfigAllFilters,
    };

    const marketPremiumSubscription = getSubscriptionForOtherProducts(
      ProductType.MarketPremium,
      pagesConfig,
      defaultClientId,
    );
    const marketPremiumSubscribedCountries = mapSubscribedCountries(marketPremiumSubscription);
    resetSideFilter();

    /**
     * The reset sequence below is very important, this is due to the massive useEffect that we have in the page
     */
    // if update to react 18 and above, can remove call to batch function
    batch(async () => {
      updateSideFilters(storeFilterSettings);
      updateMainFiltersRO(payload);
      updateRetailerClient(
        constructRetailerClientState(defaultClientId, subscribedCountries, selectedClientName, PageName.EXPLORE),
      );
      updateInitialFilters({ initialFilters: filtersData });
      if (marketPremiumSubscribedCountries.length > 0) {
        updateInitialFiltersMarketPremium({ initialFilters: filtersData });
        updateMarketPremiumRetailerClient(
          constructRetailerClientState(defaultClientId, subscribedCountries, selectedClientName, PageName.EXPLORE),
        );
        pagesConfig.setMarketPremiumClientId(defaultClientId);
        pagesConfig.setMarketPremiumClientCountries(subscribedCountries);
      }

      // Do not simply move the setState below, it will trigger unintended side effects
      pagesConfig.setSelectedFilters(pagesConfigAllFilters);

      pagesConfig.setClientCountries(subscribedCountries);
      pagesConfig.setClientId(defaultClientId);
      if (hitlistsSubscriptions.length !== 0) {
        pagesConfig.setHitlistsClientCountries(hitlistsSubscribedCountries);
        pagesConfig.setHitlistsClientId(defaultClientId);
      }

      setSelectedCountries(constructCountryOption(defaultCountry));
      setAppliedCountries(constructCountryOption(defaultCountry));
    });
    refetch();

    // Updates the selected period upon multibanner change.
    // Uses the latest period of the default country upon retailer change

    const countryOptions = constructCountryFilter(constructCountryOption(defaultCountry));

    if (defaultCountry) {
      let latestTimePeriodOption = getLatestPeriodByPeriodicityForSingleCountry(
        countryOptions,
        mainFiltersState,
        filtersData,
      );
      latestTimePeriodOption ??= {
        label: '',
        value: '',
        lastUpdatedDate: 0,
      };
      periodDispatchActions.updatePeriod(latestTimePeriodOption);
    }

    setLoadingOverlay(false);
  };
  const disabledPeriodicityLineChart: Periodicity[] = performanceAnalysisConfiguration.disabledPeriodicityOptions;

  const factRevenueBySelectedCurrency = useFactRevenueBySelectedCurrency(
    pagesConfig,
    appliedCountries,
    effectiveProductType,
  );

  const { data: allFeatureValues } = useSideMenuFilters({
    clientId: pagesConfig.clientId,
    timeFrame: getDefaultValueForDerivablePeriodicity(periodicity),
    selectedCountries: selectedCountries,
    selectedProducts: selectedProductGroups,
    responseConfig: exploreResponseConfig,
    appliedProductGroups,
    pagesConfig,
    initialFilters: initialFiltersState.initialFilters,
    currentTotalName: CURRENT_TOTAL_NAME,
    productType: effectiveProductType,
  });

  useFetchSalesPerformance({
    productType: effectiveProductType,
    timeFrame: periodicity,
    period,
    comparisonPeriod,
    defaultFilters: initialFiltersState.initialFilters,
    clientId: retailerClientState.clientId,
    setIsFetchingCardApiCallback(isFetchingApi: boolean) {
      setIsFetchingCardApi(isFetchingApi);
    },
    setLoadingPosFacts,
    setVolumePerformance,
    setRevenuePerformance,
    pageType: PageType.EXPLORE,
    queryCountries: countriesToQuery,
    setIsApiErrorCallback: setIsApiError,
  });

  const renderContent = () => {
    if (selectedLink.value === ExploreTabView.Leaderboard) {
      // Only render the leaderboard page when mainFilters is being set
      return (
        <Leaderboard
          filters={filters}
          periodicity={periodicity}
          period={period}
          comparisonPeriod={comparisonPeriod}
          customMonthsPeriodState={customMonthsPeriodState}
          customWeeksPeriodState={customWeeksPeriodState}
          appliedCountries={appliedCountries}
          selectedCountries={selectedCountries}
          appliedProductGroups={appliedProductGroups}
          selectedProductGroups={selectedProductGroups}
          defaultFilters={initialFiltersState.initialFilters}
          selectedGrowthType={selectedGrowthType}
          setGrowthOption={setGrowthOption}
          effectiveProductType={effectiveProductType}
          groupingEffect={groupingEffect}
          setGroupingEffect={setGroupingEffect}
          isTableView={isTableView}
          setIsTableView={setIsTableView}
          currentSortingOption={currentSortingOption}
          setCurrentSortingOption={setCurrentSortingOption}
          feature={feature}
          setFeature={setFeature}
          selectedShareType={selectedShareType}
          setSelectedShareType={setSelectedShareType}
          setIsLoading={setIsLoading}
          queryCountries={queryCountries}
          allFeatureValues={allFeatureValues}
          factRevenue={factRevenueBySelectedCurrency}
          volume={currentVolume}
          revenuePerformance={revenuePerformance}
          volumePerformance={volumePerformance}
          loadingPosFacts={loadingPosFacts}
          isFetchingCardApi={isFetchingCardApi}
          saveViewFilter={getSaveViewFilters()}
          retailerClients={retailerClients}
          isApiError={isApiError}
          setIsApiError={setIsApiError}
        />
      );
    }

    if (selectedLink.value === ExploreTabView.Trends) {
      return (
        <Trends //refer component PerformanceAnalysisCard and explore Leaderboard
          filters={filters}
          periodicity={periodicity}
          period={period}
          comparisonPeriod={comparisonPeriod}
          appliedCountries={appliedCountries}
          selectedCountries={selectedCountries}
          appliedProductGroups={appliedProductGroups}
          selectedProductGroups={selectedProductGroups}
          defaultFilters={initialFiltersState.initialFilters}
          selectedGrowthType={selectedGrowthType}
          setGrowthOption={setGrowthOption}
          effectiveProductType={effectiveProductType}
          feature={feature}
          setFeature={setFeature}
          selectedShareType={selectedTrendsShareType}
          setSelectedShareType={setSelectedTrendsShareType}
          selectedSaleType={selectedSaleType}
          setSelectedSaleType={setSelectedSaleType}
          selectedAbsoluteType={selectedAbsoluteType}
          setSelectedAbsoluteType={setSelectedAbsoluteType}
          currentSortingOption={currentSortingOption}
          setCurrentSortingOption={setCurrentSortingOption}
          setIsLoading={setIsLoading}
          queryCountries={queryCountries}
          isLineChartView={isLineChartView}
          setIsLineChartView={setIsLineChartView}
          allFeatureValues={allFeatureValues}
          factRevenue={factRevenueBySelectedCurrency}
          volume={currentVolume}
          revenuePerformance={revenuePerformance}
          volumePerformance={volumePerformance}
          loadingPosFacts={loadingPosFacts}
          isFetchingCardApi={isFetchingCardApi}
          exploreViewState={getSaveViewFilters()}
          isApiError={isApiError}
        />
      );
    }

    if (selectedLink.value === ExploreTabView.Segments) {
      return (
        <Segments
          retailerClientState={retailerClientState}
          allFeatureValues={allFeatureValues}
          productType={effectiveProductType}
          allFilters={allFilters}
          appliedCountries={appliedCountries}
          appliedProductGroups={appliedProductGroups}
          selectedProductGroups={selectedProductGroups}
          period={period}
          periodType={periodicity}
          comparisonPeriod={comparisonPeriod}
          showPerformanceWithin={feature}
          setShowPerformanceWithin={setFeature}
          selectedShareType={selectedShareType}
          currentSortingOption={currentSortingOption}
          setCurrentSortingOption={setCurrentSortingOption}
          volume={currentVolume}
          pgsByAppliedCountries={pgsByAppliedCountry}
          selectedGrowthType={selectedGrowthType}
          countriesToQuery={countriesToQuery}
          factRevenue={factRevenueBySelectedCurrency}
          revenuePerformance={revenuePerformance}
          volumePerformance={volumePerformance}
          loadingPosFacts={loadingPosFacts}
          isRestoreFromSavedView={isRestoreFromSavedView}
          setIsRestoreFromSavedView={setIsRestoreFromSavedView}
          setIsLoading={setIsLoading}
          exploreViewState={getSaveViewFilters()}
          segmentByFeatureParent={segmentByFeature}
          setSegmentByFeatureParent={setSegmentByFeature}
          segmentKpiParent={kpi}
          isApiError={isApiError}
        />
      );
    }
  };

  const getSaveViewFilters = (): ExploreViewState => {
    const handledTimeFrame = getPeriodicityForMATAndYTD(period, periodicity);
    return {
      periodicity: handledTimeFrame.periodicity,
      matYtdTimePeriodLabel: handledTimeFrame.matYtdLabel,
      comparisonPeriod: isBaseTimePeriodFormat(comparisonPeriod)
        ? RelativePeriodLabel.PreviousPeriod
        : RelativePeriodMapping[comparisonPeriod as RelativePeriod],
      growthType: selectedGrowthType.charAt(0).toUpperCase() + selectedGrowthType.slice(1),
      appliedFilters: pagesConfig.appliedFilters[PageType.EXPLORE].selectedFilters,
      retailerClientState: retailerClientState,
      initialFilterState: initialFiltersState,
      appliedCountries: appliedCountries,
      hitlistRetailerClientState: hitlistsRetailerClientState,
      hitlistInitialFilterState: initialFiltersProductsState,
      selectedShareType: selectedShareType,
      mainFilter: mainFiltersState,
      sorting: currentSortingOption,
      productMainFilterState: mainFilterStateProducts,
      feature: feature,
      leaderboardIsTableView: isTableView,
      trendsIsLineChartView: isLineChartView,
      trendsSelectedSaleType: selectedSaleType,
      selectedAbsoluteType: isCurrentTabTrendsTab() ? selectedAbsoluteType : null,
      segmentByFeature: isCurrentTabSegmentsTab() ? segmentByFeature : null,
      segmentKpi: isCurrentTabSegmentsTab() ? kpi : null,
      sortByOption: pagesConfig.sortByOptionsPerformanceAnalysisStackedChart,
    };
  };
  const isCurrentTabTrendsTab = () => {
    return selectedLink.value === ExploreTabView.Trends;
  };

  const isCurrentTabSegmentsTab = () => {
    return selectedLink.value === ExploreTabView.Segments;
  };
  const getSaveViewTrackingObject = () => {
    return {
      access: pagesConfig.access,
      locationId: appliedCountries.map((country) => country.label),
      pageName: PageName.EXPLORE,
      itemGroup:
        appliedProductGroups.map((pg) => pg.label).length === 0
          ? 'ALL'
          : appliedProductGroups.map((pg) => pg.label).toString(),
      section: TrackingGeneralProp.SECTION_TRACKING_PROP,
      subSection: PageName.EXPLORE,
    };
  };

  // props to pass local setState to restore saved views hooks
  const restoreLocalViewProps: ExploreTabRestoreLocalSavedViewProps = {
    productType: effectiveProductType,
    setIsSubscribed,
    setTimeFrame: updatePeriodicity,
    setPeriod: updatePeriod,
    setRelativePeriod: updateComparisonPeriod,
    selectedLink,
    setCurrentSortingOption,
    setGrowthOption,
    setSelectedShareType,
    setIsTableView,
    setIsLineChartView,
    setFilters: () => {},
    setSelectedCountries,
    setAppliedCountries,
    setDefaultFilters: () => {},
    setSelectedProductGroupOptions: () => {},
    setAppliedProductGroupOptions: () => {},
    setCustomMonthsPeriodState: updateCustomMonthsPeriodState,
    setCustomWeeksPeriodState: updateCustomWeeksPeriodState,
    setFeature,
    setSelectedSaleType,
    setIsRestoreFromSavedView,
    setSelectedAbsoluteType,
    setSortingOption: setCurrentSortingOption,
    setSegmentByFeature: setSegmentByFeature,
    setSegmentKpi: setKpi,
  };

  // Call restore saved views hooks to restore the Explore page views
  useRestoreExploreSavedView(restoreLocalViewProps);

  const resetSideFilter = () => {
    removeUnsavedFilters(pagesConfig);
    pagesConfig.setClearFilters(false);
    pagesConfig.setApplyFilters(false);
  };

  const filteredScopeData = getCountryFilterPairsByTimeFrame(pagesConfig.pgsByCountry, periodicity);

  const applyTimeFramePickerCallback: (
    appliedPeriodicity: Periodicity,
    appliedPeriod: BaseTimePeriod,
    appliedRelativePeriod: RelativePeriod | BaseTimePeriod,
  ) => void = (appliedPeriodicity, appliedPeriod, appliedRelativePeriod) => {
    applyTimeFramePicker({
      givenPeriodicity: appliedPeriodicity,
      givenPeriod: appliedPeriod,
      givenComparison: appliedRelativePeriod,
      pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
      pageName: retailerClientState.pageViewing,
      pagesConfig: pagesConfig,
      countryChanged: false,
      sideFiltersState: sideFiltersState,
      retailerClientState: retailerClientState,
      track: track,
      setTimeFrame: updatePeriodicity,
      setPeriod: updatePeriod,
      setRelativePeriod: updateComparisonPeriod,
      setCountryChanged: () => {},
      setCustomMonthPeriodState: updateCustomMonthsPeriodState,
      setCustomWeekPeriodState: updateCustomWeeksPeriodState,
    });

    const payload = {
      selectedGrowthType: selectedGrowthType,
      country: appliedCountries[0].value,
    };
    updateMainFiltersRO(payload);

    const mainFilters = {
      timeFrame: periodicity,
      period: period,
      comparisonPeriod: comparisonPeriod,
      selectedGrowthType: selectedGrowthType,
      customMonthsPeriodState: customMonthsPeriodState,
      customWeeksPeriodState: customWeeksPeriodState,
    };
    //this is to bring the selected timeframe to TA page
    updateMainFiltersMarketPremium({
      ...mainFilters,
    });
    // when timeFrame is changed, whatever that is not applied in filter tray will be reset, selected countries and product groups reset
    removeUnsavedFilters(pagesConfig);
  };

  return (
    <div>
      <Page>
        <Page.Title>
          <div className="pt-8">
            <PageHeader
              retailerClients={retailerClients}
              onChangeHandler={onChangeHandler}
              productType={effectiveProductType}
            >
              <div className="grid grid-cols-3">
                <div className="col-start-1">
                  <h1 className="text-heading text-4xl font-normal m-0">
                    <span>Explore</span>
                    <PageInfo title="Explore" content={<TooltipsTextComponent />} />
                  </h1>
                </div>

                <div className="col-start-5">
                  {isAuthorizedUser && isSubscribed && (
                    <div className="flex justify-end">
                      <ExploreCopyViewButton
                        exploreViewState={getSaveViewFilters()}
                        copyViewTracking={getSaveViewTrackingObject()}
                      />
                      <FilterToggleButton onClick={onFiltersTrayClickHandler} testId="filter-button" />
                    </div>
                  )}
                </div>
              </div>
            </PageHeader>
          </div>
        </Page.Title>
        <Page.Content>
          <InfoModal />
          <div key={pagesConfig.pageName}>
            <TabNav
              preNavigateAction={resetSideFilter}
              selectedLink={selectedLink}
              links={explorLinkConfig}
              setSelectedLink={setSelectedLink}
            />
          </div>
          <LoadingOverlay loadingOverlay={loadingOverlay} />
          <div className="grid grid-flow-col auto-cols-max gap-x-3 pt-6 pb-4">
            <TimeFramePickerWrapper
              isPeriodOptionsDisabled={false}
              isTimeFramePickerDisabled={false}
              disabledPeriodicityOptions={
                selectedLink.value === ExploreTabView.Trends ? disabledPeriodicityLineChart : undefined //explore-trends page will allow monthly/weekly period options
              }
              currentPeriod={period}
              currentPeriodicity={periodicity}
              currentComparison={comparisonPeriod}
              periodOptions={timePeriodOptions}
              applyPickerCallback={applyTimeFramePickerCallback}
              customMonthsPeriod={customMonthsPeriodState}
              customWeeksPeriod={customWeeksPeriodState}
              appliedCountries={appliedCountries}
              appliedProductGroups={appliedProductGroups}
              isPeriodOptionsDisabledPredicate={(selectedPeriodicity) =>
                selectedLink.value === ExploreTabView.Trends &&
                excludedPeriodicityForLineChart.includes(selectedPeriodicity)
              }
            />
            <ComparisonGrowthSelector
              growthOptions={filters.comparisonGrowth}
              growthOption={(growth: GrowthTypes) => setGrowthOption(growth)}
              selectedGrowthOption={selectedGrowthType}
              isDisabled={false}
            />
          </div>
          <div className="pb-4">
            <FilterSelectionNote
              selectedPeriod={period}
              comparisonPeriodType={comparisonPeriod}
              selectedPeriodicity={periodicity}
              scopeMetadata={filteredScopeData[0]?.scopeMetadata}
            />
          </div>
          <div>
            {!isLoading && (
              <>
                {isSelectMultiCountry && <CountryAvailabilityBanner />}
                {<PgAvailabilityBanner />}
                <WarningFeatureValueContainer periodicity={periodicity} period={period} pageName={PageName.EXPLORE} />
                <WarningProductGroupFilterContainer pagesConfig={pagesConfig} />
              </>
            )}
            <div className="pt-4">
              <FilterChips pgListByCountry={pgsByAppliedCountry} selectedTimeFrame={periodicity} />
            </div>
          </div>
          {isAuthorizedUser && isSubscribed ? (
            <Suspense fallback={<feedback.DotsLoader />}>{renderContent()}</Suspense>
          ) : (
            Unauthorized()
          )}
        </Page.Content>
        <Page.Footer> </Page.Footer>
      </Page>
    </div>
  );
};

export default Explore;
